/**
 * State API - Frontend utilities for state check-in persistence
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3100';

/**
 * Save current state to backend
 * @param {string} token - Auth token
 * @param {Object} state - { focus, feeling, location }
 * @returns {Promise<Object>} Saved state with ID
 */
export async function saveState(token, state) {
  const response = await fetch(`${API_BASE}/api/user/state/current`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      focus: state.focus,
      feeling: state.feeling,
      location: state.location
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to save state' }));
    throw new Error(error.error || 'Failed to save state');
  }

  return response.json();
}

/**
 * Get current state from backend
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Current state with staleness info
 */
export async function getCurrentState(token) {
  const response = await fetch(`${API_BASE}/api/user/state/current`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get state' }));
    throw new Error(error.error || 'Failed to get state');
  }

  return response.json();
}

/**
 * Check if current state is stale (for prompting UI)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Staleness info with prompt
 */
export async function checkStaleness(token) {
  const response = await fetch(`${API_BASE}/api/user/state/staleness`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to check staleness' }));
    throw new Error(error.error || 'Failed to check staleness');
  }

  return response.json();
}

/**
 * Get state history
 * @param {string} token - Auth token
 * @param {Object} options - { limit, days }
 * @returns {Promise<Object>} State history with stats
 */
export async function getStateHistory(token, options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit);
  if (options.days) params.set('days', options.days);

  const url = `${API_BASE}/api/user/state/history${params.toString() ? '?' + params : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get history' }));
    throw new Error(error.error || 'Failed to get history');
  }

  return response.json();
}
